import PropTypes from "prop-types";
import { noCase } from "change-case";
import { useState } from "react";
// @mui
import {
  Box,
  Stack,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  IconButton,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from "@mui/material";
// utils
import { fToNow } from "../../../utils/formatTime";
// _mock_
import { _notifications } from "../../../_mock/arrays";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import MenuPopover from "../../../components/menu-popover";
import { IconButtonAnimate } from "../../../components/animate";
import { useLocales } from "../../../locales";

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { translate } = useLocales();
  const [openPopover, setOpenPopover] = useState(null);

  const [notifications, setNotifications] = useState(_notifications);

  const pathname = window.location.pathname;

  const totalUnRead = notifications.filter(
    (item) => item.isUnRead === true
  ).length;

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleMarkAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        isUnRead: false,
      }))
    );
  };

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? "primary" : "default"}
        onClick={handleOpenPopover}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <img src="/assets/icons/navbar/notification_ic.svg" alt="" />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{
          marginTop: "25px",
          borderRadius: "0px",
          /*  paddingLeft: "22px",
          paddingRight: "22px",
          paddingTop: "25px",*/
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "391px",
            alignItems: "center",
            paddingLeft: "22px",
            paddingRight: "22px",
            paddingTop: "25px",
            marginBottom: "10px",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, lineHeight: "21px" }}
            >
              {translate("Notifications")}
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {`${translate("you_have")} ${totalUnRead} ${translate(
                "unread_messages"
              )} `}
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title={translate("mark_all_as_read")}>
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider borderStyle="dash" />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
          <List
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  py: 1,
                  px: 2.5,
                  typography: "overline",
                  marginTop: "10px",
                }}
              >
                {translate("new")}
              </ListSubheader>
            }
          >
            {notifications.slice(0, 2).map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))}
          </List>

          <List
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{ py: 1, px: 2.5, typography: "overline" }}
              >
                {translate("before_that")}
              </ListSubheader>
            }
          >
            {notifications.slice(2, 5).map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple>
            {translate("view_all")}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string,
    avatar: PropTypes.node,
    type: PropTypes.string,
    title: PropTypes.string,
    isUnRead: PropTypes.bool,
    description: PropTypes.string,
    createdAt: PropTypes.instanceOf(Date),
  }),
};

function NotificationItem({ notification }) {
  const { translate } = useLocales();
  const { avatar, title } = renderContent(notification);
  console.log(fToNow(notification.createdAt));

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        ...(notification.isUnRead && {
          bgcolor: "action.selected",
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: "background.neutral" }}>{avatar}</Avatar>
      </ListItemAvatar>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography>{title}</Typography>
      </Box>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography sx={{ color: "#535353", fontWeight: 500, fontSize: "12px" }}>
      {notification.title}

      <Typography
        component="p"
        sx={{ color: "#535353", fontWeight: 400, fontSize: "10px" }}
      >
        &nbsp; {noCase(notification.description)}
      </Typography>
    </Typography>
  );

  if (notification.type === "warning") {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="/assets/icons/notification/ic_warnning.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === "succesed") {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="/assets/icons/notification/ic_success.svg"
        />
      ),
      title,
    };
  }

  return {
    avatar: notification.avatar ? (
      <img alt={notification.title} src={notification.avatar} />
    ) : null,
    title,
  };
}
